import { CommunityId, daysAgo } from "@sp-crm/core";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import { FindOrCreateCommunityInsightsMutation } from "generated/graphql";
import React, { useCallback } from "react";
import { useCanRequestCommunityAvailability } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { CommunityRequestListingSignupPanel } from "./community-request-listing-signup-panel";

interface CommunityAvailabilityMissingProps {
    listing: FindOrCreateCommunityInsightsMutation["findOrCreateCommunityInsights"];
    communityId: CommunityId;
}

export const CommunityAvailabilityMissing = (
    props: CommunityAvailabilityMissingProps,
) => {
    const { listing, communityId } = props;
    const canRequestAvailability = useCanRequestCommunityAvailability();
    const [isRequestDialogOpen, setIsRequestDialogOpen] = React.useState(false);
    const [hasRequestedAvailability, setHasRequestedAvailability] = React.useState(false);

    const requestAvailability = useCallback(() => {
        handleEvent("request-community-availability", {
            communityId,
            listingId: listing.id,
        });
        setIsRequestDialogOpen(true);
    }, [communityId, listing.id]);

    const closeRequestDialog = useCallback(() => {
        setIsRequestDialogOpen(false);
    }, []);

    const onRequestSuccess = useCallback(() => {
        setHasRequestedAvailability(true);
        closeRequestDialog();
        handleEvent("request-community-availability-success", {
            communityId,
            listingId: listing.id,
        });
    }, [closeRequestDialog, communityId, listing.id]);

    return (
        <>
            {isRequestDialogOpen ? (
                <CommunityRequestListingSignupPanel
                    communityId={communityId}
                    onDismiss={closeRequestDialog}
                    onSuccess={onRequestSuccess}
                />
            ) : null}
            <div className="space-y-4 mt-2">
                <div className="text-sm text-gray-500">
                    {listing.name} has not yet reported any availability to Community
                    Insights.
                </div>
                {canRequestAvailability ? (
                    <div>
                        <PrimaryButton onClick={requestAvailability}>
                            Request Availability
                        </PrimaryButton>
                    </div>
                ) : null}
                {hasRequestedAvailability ? (
                    <InlineBanner type="info">Availability request sent!</InlineBanner>
                ) : null}
                {listing.availability?.lastRequestedAt ? (
                    <div className="mt-4 space-y-2">
                        <p className="text-gray-500 text-sm">
                            Last requested{" "}
                            {daysAgo(listing.availability?.lastRequestedAt)}.
                        </p>
                    </div>
                ) : null}
            </div>
        </>
    );
};
